// frontend/src/pages/PerfilPage.jsx
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import authService from '../services/authService';
import contentService from '../services/contentService';
import './PerfilPage.css';

export default function PerfilPage() {
  const user = authService.getUser();
  const location = useLocation();

  // ── Meta OAuth state ──────────────────────────────────────────────────────
  const [metaEstadoIG, setMetaEstadoIG] = useState(null);   // { conectado, cuentas }
  const [metaEstadoFB, setMetaEstadoFB] = useState(null);   // { conectado, paginas }
  const [metaLoading, setMetaLoading] = useState(false);
  const [metaMsg, setMetaMsg] = useState('');

  useEffect(() => {
    // Leer estado actual de la conexión Meta (IG + FB)
    contentService.getInstagramMetaEstado()
      .then(r => setMetaEstadoIG(r.data))
      .catch(() => setMetaEstadoIG({ conectado: false, cuentas: [] }));
    contentService.getFacebookMetaEstado()
      .then(r => setMetaEstadoFB(r.data))
      .catch(() => setMetaEstadoFB({ conectado: false, paginas: [] }));

    // Detectar callback de Meta (meta_connected=1 o meta_error=...)
    const params = new URLSearchParams(location.search);
    if (params.get('meta_connected') === '1') {
      setMetaMsg('✅ Cuenta conectada correctamente. Instagram y Facebook sincronizados.');
    } else if (params.get('meta_error')) {
      setMetaMsg(`⚠️ Error al conectar: ${params.get('meta_error')}`);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMetaConectar = async () => {
    setMetaLoading(true);
    try {
      const r = await contentService.getMetaOAuthUrl();
      window.location.href = r.data.url;
    } catch {
      setMetaMsg('⚠️ No se pudo obtener la URL de autorización. Verifica META_APP_ID en settings.');
      setMetaLoading(false);
    }
  };

  const handleMetaSincronizar = async () => {
    setMetaLoading(true);
    try {
      const [rIG, rFB] = await Promise.all([
        contentService.sincronizarInstagramMeta(),
        contentService.sincronizarFacebookMeta(),
      ]);
      setMetaEstadoIG(rIG.data);
      setMetaEstadoFB(rFB.data);
      setMetaMsg('✅ Cuentas sincronizadas.');
    } catch {
      setMetaMsg('⚠️ Error al sincronizar cuentas.');
    } finally {
      setMetaLoading(false);
    }
  };

  const handleMetaDesconectar = async () => {
    if (!window.confirm('¿Desconectar la cuenta de Meta? Se perderán Instagram y Facebook.')) return;
    setMetaLoading(true);
    try {
      await contentService.desconectarInstagramMeta();
      setMetaEstadoIG({ conectado: false, cuentas: [] });
      setMetaEstadoFB({ conectado: false, paginas: [] });
      setMetaMsg('Cuenta desconectada.');
    } catch {
      setMetaMsg('⚠️ Error al desconectar.');
    } finally {
      setMetaLoading(false);
    }
  };

  const metaConectado = metaEstadoIG?.conectado || metaEstadoFB?.conectado;

  const [form, setForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [showCurrentPwd, setShowCurrentPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm_password) {
      setError('Las contraseñas nuevas no coinciden.');
      return;
    }
    if (form.new_password.length < 8) {
      setError('La nueva contraseña debe tener al menos 8 caracteres.');
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await authService.changePassword(
        form.current_password,
        form.new_password,
        form.confirm_password
      );
      setSuccess('¡Contraseña actualizada correctamente!');
      setForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      const msg = err?.response?.data?.error || 'Error al cambiar la contraseña.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = () => {
    if (!user) return '?';
    const first = user.first_name?.[0] || '';
    const last = user.last_name?.[0] || '';
    return (first + last).toUpperCase() || user.username?.[0]?.toUpperCase() || '?';
  };

  const strengthLevel = (pwd) => {
    if (!pwd) return 0;
    let score = 0;
    if (pwd.length >= 8) score++;
    if (pwd.length >= 12) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;
    return score;
  };

  const strength = strengthLevel(form.new_password);
  const strengthLabels = ['', 'Muy débil', 'Débil', 'Regular', 'Buena', 'Fuerte'];
  const strengthColors = ['', '#ef4444', '#f97316', '#f59e0b', '#10b981', '#059669'];

  return (
    <div className="perfil-page">
      <div className="perfil-container">

        {/* ── Card de datos del usuario ── */}
        <div className="perfil-card user-card">
          <div className="user-avatar">
            {getInitials()}
          </div>
          <div className="user-info">
            <h2 className="user-name">
              {user?.full_name || user?.username || 'Usuario'}
            </h2>
            {user?.role && (
              <span className="user-role-badge">{user.role}</span>
            )}
          </div>

          <div className="user-fields">
            <div className="user-field">
              <span className="field-icon">👤</span>
              <div className="field-content">
                <label>Nombre completo</label>
                <span>{user?.full_name || '—'}</span>
              </div>
            </div>
            <div className="user-field">
              <span className="field-icon">✉️</span>
              <div className="field-content">
                <label>Correo electrónico</label>
                <span>{user?.email || '—'}</span>
              </div>
            </div>
            {user?.cuenta_activa && (
              <div className="user-field">
                <span className="field-icon">🏢</span>
                <div className="field-content">
                  <label>Cuenta activa</label>
                  <span>{user.cuenta_activa.nombre}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Card de cambio de contraseña ── */}
        <div className="perfil-card password-card">
          <div className="card-header">
            <span className="card-header-icon">🔒</span>
            <div>
              <h3>Cambiar contraseña</h3>
              <p>Elige una contraseña segura de al menos 8 caracteres.</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="password-form">

            {/* Contraseña actual */}
            <div className="form-group">
              <label htmlFor="current_password">Contraseña actual</label>
              <div className="input-wrapper">
                <input
                  id="current_password"
                  type={showCurrentPwd ? 'text' : 'password'}
                  name="current_password"
                  value={form.current_password}
                  onChange={handleChange}
                  placeholder="Ingresa tu contraseña actual"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="toggle-pwd"
                  onClick={() => setShowCurrentPwd(v => !v)}
                  tabIndex={-1}
                >
                  {showCurrentPwd ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            {/* Nueva contraseña */}
            <div className="form-group">
              <label htmlFor="new_password">Nueva contraseña</label>
              <div className="input-wrapper">
                <input
                  id="new_password"
                  type={showNewPwd ? 'text' : 'password'}
                  name="new_password"
                  value={form.new_password}
                  onChange={handleChange}
                  placeholder="Mínimo 8 caracteres"
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="toggle-pwd"
                  onClick={() => setShowNewPwd(v => !v)}
                  tabIndex={-1}
                >
                  {showNewPwd ? '🙈' : '👁️'}
                </button>
              </div>
              {/* Barra de fuerza */}
              {form.new_password && (
                <div className="strength-bar-wrapper">
                  <div className="strength-bar">
                    {[1,2,3,4,5].map(i => (
                      <div
                        key={i}
                        className="strength-segment"
                        style={{ background: i <= strength ? strengthColors[strength] : '#e5e7eb' }}
                      />
                    ))}
                  </div>
                  <span className="strength-label" style={{ color: strengthColors[strength] }}>
                    {strengthLabels[strength]}
                  </span>
                </div>
              )}
            </div>

            {/* Confirmar contraseña */}
            <div className="form-group">
              <label htmlFor="confirm_password">Confirmar nueva contraseña</label>
              <div className="input-wrapper">
                <input
                  id="confirm_password"
                  type={showConfirmPwd ? 'text' : 'password'}
                  name="confirm_password"
                  value={form.confirm_password}
                  onChange={handleChange}
                  placeholder="Repite la nueva contraseña"
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="toggle-pwd"
                  onClick={() => setShowConfirmPwd(v => !v)}
                  tabIndex={-1}
                >
                  {showConfirmPwd ? '🙈' : '👁️'}
                </button>
              </div>
              {form.confirm_password && form.new_password !== form.confirm_password && (
                <span className="field-error">Las contraseñas no coinciden</span>
              )}
            </div>

            {/* Mensajes */}
            {error && (
              <div className="alert alert-error">
                <span>⚠️</span> {error}
              </div>
            )}
            {success && (
              <div className="alert alert-success">
                <span>✅</span> {success}
              </div>
            )}

            <button
              type="submit"
              className="btn-save-password"
              disabled={loading || !form.current_password || !form.new_password || !form.confirm_password}
            >
              {loading ? (
                <><span className="spinner" /> Guardando...</>
              ) : (
                <>🔒 Actualizar contraseña</>
              )}
            </button>
          </form>
        </div>

        {/* ── Card de conexión Meta (Instagram + Facebook) ── */}
        <div className="perfil-card" style={{ marginTop: 0 }}>
          <div className="card-header">
            <span className="card-header-icon">🔗</span>
            <div>
              <h3>Conexión Meta (Instagram + Facebook)</h3>
              <p>Conecta una vez para obtener métricas completas de Instagram (alcance, impresiones) y de la página de Facebook (fans, posts, insights).</p>
            </div>
          </div>

          {metaMsg && (
            <div className={`alert ${metaMsg.startsWith('✅') ? 'alert-success' : 'alert-error'}`} style={{ marginBottom: 16 }}>
              {metaMsg}
            </div>
          )}

          {metaEstadoIG === null && metaEstadoFB === null ? (
            <p style={{ color: '#9ca3af', fontSize: 14 }}>Verificando conexión...</p>
          ) : metaConectado ? (
            <div>
              {/* Cuentas Instagram */}
              {metaEstadoIG?.cuentas?.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Instagram</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {metaEstadoIG.cuentas.map(c => (
                      <div key={c.ig_user_id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
                        {c.profile_pic_url
                          ? <img src={c.profile_pic_url} alt={c.username} style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
                          : <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>📸</div>
                        }
                        <div>
                          <div style={{ fontWeight: 600, color: '#111827', fontSize: 13 }}>@{c.username || c.nombre}</div>
                          <div style={{ fontSize: 11, color: '#6b7280' }}>{c.seguidores?.toLocaleString('es-CL')} seguidores</div>
                        </div>
                        <div style={{ marginLeft: 'auto', fontSize: 11, color: '#059669', fontWeight: 600 }}>● Conectada</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Páginas Facebook */}
              {metaEstadoFB?.paginas?.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Facebook</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {metaEstadoFB.paginas.map(p => (
                      <div key={p.page_id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: '#eff6ff', borderRadius: 8, border: '1px solid #bfdbfe' }}>
                        {p.picture_url
                          ? <img src={p.picture_url} alt={p.nombre} style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
                          : <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>📘</div>
                        }
                        <div>
                          <div style={{ fontWeight: 600, color: '#111827', fontSize: 13 }}>{p.nombre}</div>
                          <div style={{ fontSize: 11, color: '#6b7280' }}>{p.fans?.toLocaleString('es-CL')} fans · {p.categoria}</div>
                        </div>
                        <div style={{ marginLeft: 'auto', fontSize: 11, color: '#2563eb', fontWeight: 600 }}>● Conectada</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button onClick={handleMetaSincronizar} disabled={metaLoading}
                  style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #d1d5db', background: '#f9fafb', color: '#374151', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>
                  {metaLoading ? 'Sincronizando...' : 'Sincronizar cuentas'}
                </button>
                <button onClick={handleMetaConectar} disabled={metaLoading}
                  style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>
                  Reconectar / Renovar token
                </button>
                <button onClick={handleMetaDesconectar} disabled={metaLoading}
                  style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #fca5a5', background: '#fff', color: '#ef4444', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>
                  Desconectar
                </button>
              </div>
            </div>
          ) : (
            <div>
              <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 14 }}>
                Al conectar, Meta pedirá autorización para leer las métricas de Instagram (alcance, impresiones, stories)
                y de la página de Facebook (fans, posts, insights de audiencia). El token dura ~60 días y se puede renovar reconectando.
              </p>
              <button onClick={handleMetaConectar} disabled={metaLoading}
                style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: '#1877F2', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                {metaLoading ? 'Redirigiendo...' : '📘 Conectar con Facebook / Instagram'}
              </button>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
